import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Eye, Brain, Baby, Glasses, HeartPulse, Sparkles, Ear, CheckCircle2, ArrowRight, Clock, Users, Zap, Activity, EyeOff } from "lucide-react";
import VisualCheckup from "../components/VisualCheckup";

const SERVICES_DETAIL = [
  {
    slug: "eficacia-visual",
    icon: Eye,
    title: "Evaluación de eficacia visual",
    subtitle: "Saber cómo ve tu sistema visual",
    intro: "Un examen exhaustivo que evalúa la eficacia con la que trabaja tu sistema visual. Más allá de medir la graduación, analizamos cómo se coordinan tus dos ojos, cómo enfocan, cómo se mueven y cómo procesan la información en lejos y en cerca.",
    benefits: [
      "Saber el estado real de tu visión más allá de la graduación",
      "Entender por qué te cansas, te duele la cabeza o te cuesta leer en cerca",
      "Una recomendación clara: si necesitas gafa, terapia visual o solo seguimiento",
      "Un informe detallado y comprensible, que también puedes compartir con tu colegio, oftalmólogo u otro profesional",
      "Aplicable a niños, adolescentes y adultos — adaptado a la edad",
    ],
    includes: [
      "Salud visual externa e interna",
      "Agudeza visual (lejos y cerca)",
      "Refracción para prescripción óptica",
      "Movimientos oculares: fijación, seguimientos y sacádicos",
      "Habilidades acomodativas (capacidades y flexibilidad)",
      "Habilidades binoculares: fusión, posición de ejes visuales, convergencia/divergencia, relación acomodación-convergencia, flexibilidad binocular",
      "Estereopsis (visión en profundidad)",
      "Dominancia ocular",
      "Línea media visual",
      "Integración centro-periferia",
      "Informe explicativo detallado entregado en ~10 días",
    ],
    for: "Cualquier persona que quiera saber el estado real de su visión. Muy indicado si notas fatiga visual, dolores de cabeza, visión borrosa, dificultad en la lectura, bajo rendimiento académico, deportivo o laboral. Adecuado para niños, adolescentes y adultos.",
    duration: "Informe en 10 días",
  },
  {
    slug: "procesamiento-visual-auditivo",
    icon: Brain,
    title: "Evaluación de procesamiento visual y auditivo",
    subtitle: "Cuando la dificultad va más allá de ver",
    intro: "Cuando un niño tiene dificultades en el aprendizaje, se cansa al leer, pierde la atención o lo escolar le cuesta más de la cuenta, muchas veces la raíz no está en su esfuerzo — está en cómo su cerebro procesa lo que ve y lo que oye. Esta evaluación nos da una fotografía clara de qué habilidades están sólidas y cuáles necesitan acompañamiento, para diseñar un plan a su medida.",
    benefits: [
      "Saber por qué le cuesta leer, atender o seguir el ritmo — vamos a la causa, no al síntoma",
      "Un plan terapéutico personalizado por edad y curso equivalente",
      "Un informe claro que entiendes tú y que entiende también el colegio u otros profesionales",
      "Conferencia online con el optometrista para revisar diagnóstico y pronóstico (recomendamos la presencia de ambos padres)",
      "Tranquilidad: saber qué hacer y por dónde empezar",
    ],
    includes: [
      "Habilidades visuales espaciales: lateralidad, direccionalidad, integración bilateral",
      "Percepción y forma: discriminación visual, relaciones visuo-espaciales, constancia de formas, figura-fondo, cierre visual",
      "Reproducción visual de formas: integración visuo-motora, copia de frase, coordinación ojo-mano, figura de Rey",
      "Memoria visual y secuencial + integración ruta visual-auditiva y motora",
      "Movimientos oculares: sacádicos y seguimientos",
      "Habilidades lectoras: velocidad y comprensión lectora",
      "Campos visuales funcionales y reflejos primitivos",
      "Test de visuo-cognición y pruebas iniciales de WIVI (IA interactiva)",
      "Parte auditiva: agudeza, discriminación, umbrales dolorosos, distorsiones, lateralidad, tiempo de respuesta, errores y fatiga auditiva",
      "Informe extenso con implicaciones y recomendaciones terapéuticas",
      "Conferencia online con el optometrista para revisar diagnóstico y pronóstico (se recomienda presencia de ambos padres)",
      "Copias del informe para colegio u otros profesionales bajo petición",
    ],
    for: "Niños y adolescentes con dificultades en el aprendizaje, bajo rendimiento académico, lectura lenta o cansada, falta de atención, memoria o coordinación. También en casos de lesión cerebral adquirida (ictus, TCE, patologías neurológicas).",
    duration: "Toda una mañana (9:30 - 13:15) · Informe en ~2 semanas",
  },
  {
    slug: "terapia-visual",
    icon: Brain,
    title: "Terapia visual · Optometría comportamental",
    subtitle: "Un programa individual para reeducar la visión",
    intro: "La terapia visual es un tratamiento neurofuncional que crea nuevas conexiones sinápticas mediante ejercicios dirigidos por un optometrista comportamental. Permite al paciente procesar correctamente la información visual y corregir sus problemas de visión.",
    benefits: [
      "Resolver la causa, no solo compensarla con una gafa",
      "Leer, estudiar o trabajar sin cansancio, dolor de cabeza ni visión doble",
      "Mejorar la coordinación de ambos ojos, el enfoque y los movimientos oculares",
      "Programa 100% personalizado, con ejercicios en consulta y guiados en casa",
      "Sin medicación, no invasivo y eficaz a cualquier edad gracias a la neuroplasticidad",
    ],
    includesTitle: "Cómo es la terapia",
    includes: [
      "Sesiones individuales en consulta de 45 minutos, 1-2 veces por semana",
      "Ejercicios diarios en casa de 15 minutos",
      "Seguimiento quincenal de los ejercicios pautados",
      "Ajustes continuos del nivel y la progresión",
      "Tratamiento no invasivo, sin medicación y amable",
    ],
    for: "Ambliopía (ojo vago), estrabismo, dificultades en el aprendizaje, visión doble, problemas de enfoque, oculomotricidad, estereopsis reducida o inexistente, lesión cerebral adquirida, trastornos neurológicos. Niños y adultos — la neuroplasticidad permite mejorar a cualquier edad.",
    duration: "Desde 3 meses hasta 2 años según el caso",
  },
  {
    slug: "ambliopia-estrabismo",
    icon: EyeOff,
    title: "Ambliopía y estrabismo",
    subtitle: "El \"ojo vago\" no es vago — y se puede tratar a cualquier edad",
    intro: "La ambliopía (popularmente \"ojo vago\") y el estrabismo son dos de las condiciones visuales más comunes y peor entendidas. Los avances neurocientíficos han demostrado que el ojo no es vago: lo que ocurre es que el cerebro no está coordinando correctamente los dos ojos. Por eso el parche solo, aunque sigue siendo lo más recetado, aborda el síntoma y no la causa. Trabajamos con un enfoque actualizado, basado en evidencia, que combina terapia visual, prescripción óptica personalizada y, cuando es necesario, oclusión con filtros graduados.",
    benefits: [
      "Recuperar la visión binocular real — que los dos ojos trabajen juntos, no por separado",
      "Mejor lectura, mayor confort visual y desaparición de la fatiga al estudiar o trabajar",
      "Visión en 3D y mejor cálculo de distancias (clave para conducir, deporte y vida diaria)",
      "Tratamiento posible a cualquier edad gracias a la plasticidad cerebral — también en adultos",
      "En muchos casos no se necesita parche: la terapia visual enseña a los dos ojos a trabajar juntos",
    ],
    adaptationsTitle: "Mitos vs. realidad",
    adaptations: [
      "MITO: \"Después de los 7-8 años ya no se puede recuperar la visión de un ojo ambliope\". REALIDAD: la plasticidad cerebral existe desde que nacemos hasta que morimos — el tratamiento es posible a cualquier edad.",
      "MITO: \"El único tratamiento es el parche\". REALIDAD: el parche aborda el síntoma, no la causa. En muchos casos no se necesita.",
      "MITO: \"El estrabismo se opera y ya está\". REALIDAD: la cirugía corta los músculos, pero el cerebro sigue sin saber coordinar los ojos. La terapia visual trabaja la causa.",
      "MITO: \"Cuantas más horas se lleve el parche, mejor\". REALIDAD: 4-6 horas es igual de eficaz que llevarlo más tiempo.",
      "MITO: \"Si el estrabismo apareció en los primeros meses de vida, nunca verá en 3D\". REALIDAD: con un tratamiento adecuado, sí es posible recuperar visión en 3D.",
    ],
    includes: [
      "Evaluación completa de eficacia visual y de la binocularidad",
      "Estudio del estado de la fijación, los seguimientos y los sacádicos en cada ojo",
      "Valoración del estrabismo: tipo, constancia, ángulo, dominancia",
      "Análisis de la supresión y de la cooperación entre ambos ojos",
      "Refracción precisa adaptada a la condición (lentes especiales si procede)",
      "Plan terapéutico individualizado: terapia visual, prescripción y, si corresponde, oclusión con filtros graduados",
    ],
    for: "Niños y adultos con ojo vago (ambliopía), con o sin estrabismo · Estrabismos intermitentes (que aparecen al cansarse) · Adultos a quienes les dijeron de pequeños que 'ya no había nada que hacer' · Pacientes operados de estrabismo que siguen sin coordinar bien los dos ojos · Casos en los que el parche por sí solo no ha funcionado.",
    duration: "Tratamiento individualizado · Evaluación inicial en una sesión",
  },
  {
    slug: "optometria-infantil",
    icon: Baby,
    title: "Optometría infantil",
    subtitle: "Detección temprana para un buen aprendizaje",
    intro: "Los niños no siempre saben que ven mal — piensan que todos ven como ellos. Por eso la revisión temprana es clave. Hasta un 80% del aprendizaje escolar entra por la vista: un problema visual no detectado puede confundirse con falta de atención o dificultades escolares.",
    benefits: [
      "Detectar a tiempo lo que tu hijo/a no sabe explicar",
      "Prevenir que un problema visual se confunda con falta de atención o dificultades en el cole",
      "Una experiencia amable: pruebas adaptadas por edad y con un punto lúdico",
      "Saber con certeza si necesita gafa, terapia visual o solo seguimiento",
      "Informe claro para ti y, si procede, una copia para el colegio",
    ],
    includes: [
      "Examen adaptado por edad, con pruebas lúdicas",
      "Evaluación de agudeza visual y refracción",
      "Valoración de la binocularidad y movimientos oculares",
      "Screening de ambliopía y estrabismo",
      "Estudio de habilidades visuales para el aprendizaje",
      "Informe escrito para padres y colegio si procede",
    ],
    for: "Niños/as desde los 2 años. Recomendable la primera revisión antes de los 4 años y después cada 1-2 años, aunque no haya síntomas aparentes.",
    duration: "45-60 min",
  },
  {
    slug: "contactologia",
    icon: Glasses,
    title: "Contactología avanzada",
    subtitle: "Lentes de contacto para cada tipo de mirada",
    intro: "Somos especialistas en adaptación de lentes de contacto, incluidos casos complejos donde otras ópticas no pueden ayudar. Trabajamos con los materiales y diseños más avanzados del mercado.",
    benefits: [
      "Una solución a tu medida aunque te hayan dicho que no eres candidato",
      "Casos complejos resueltos: queratocono, ojo seco, post-cirugía, alta graduación",
      "Asesoramiento honesto sobre el tipo de lente que mejor encaja en tu día a día",
      "Acompañamiento desde la primera prueba hasta las revisiones periódicas",
      "Tecnología y materiales más avanzados del mercado",
    ],
    adaptations: [
      "Ortoqueratología (Orto-K): lentes nocturnas que corrigen la miopía durante el día sin gafas",
      "Lentes blandas: esféricas, tóricas y multifocales",
      "Lentes rígidas permeables al gas (RPG)",
      "Lentes corneo-esclerales y esclerales",
      "Adaptación en queratocono y ectasias corneales",
      "Post-cirugía refractiva y post-queratoplastia",
      "Multifocales tóricas para presbicia con astigmatismo",
    ],
    includes: [
      "Topografía corneal",
      "Refracción",
      "Evaluación de la salud ocular",
      "Adaptación y control de las lentes",
      "Formación en manipulación y limpieza de las lentes de contacto",
    ],
    for: "Cualquier persona que quiera llevar lentillas, incluso si le han dicho que 'no es candidato'. Casos especiales: córneas irregulares, ojo seco severo, niños con alta graduación.",
    duration: "Primera adaptación: 1-2 sesiones",
  },
  {
    slug: "control-miopia",
    icon: HeartPulse,
    title: "Control de miopía",
    subtitle: "Frenar el avance antes de que sea tarde",
    intro: "La miopía no es solo 'necesitar gafas': a graduaciones altas aumenta el riesgo de desprendimiento de retina, glaucoma y degeneración macular. Nuestro enfoque va más allá del tratamiento óptico — buscamos la causa funcional por la que progresa (habitualmente un exceso de trabajo en visión próxima) para actuar sobre ella. Combinamos los métodos con mayor evidencia científica con terapia visual cuando la causa lo requiere, para frenar el avance en niños y adolescentes.",
    benefits: [
      "Frenar el avance de la miopía actuando sobre la causa, no solo compensando con cristales más gruesos",
      "Reducir el riesgo futuro de patologías graves de retina y mácula",
      "Acompañamiento con terapia visual cuando la causa funcional lo requiere",
      "Elegir entre varios métodos (Orto-K, lentes blandas, gafas tecnológicas) según el perfil de tu hijo/a",
      "Seguimiento exhaustivo cada 6 meses durante la etapa de crecimiento",
    ],
    adaptationsTitle: "Métodos que utilizamos",
    adaptations: [
      "Ortoqueratología (Orto-K): lentes nocturnas que remodelan la córnea",
      "Lentes de contacto blandas MiSight® y MyLo®",
      "Gafas con tecnología específica (MiYOSMART, Stellest…)",
      "Terapia visual de acompañamiento cuando hay una causa funcional implicada",
    ],
    includes: [
      "Examen completo de eficacia visual para identificar la causa de la progresión",
      "Estudio del trabajo en visión próxima: acomodación, convergencia, esfuerzo visual",
      "Análisis de la longitud axial y refracción objetiva",
      "Topografía corneal cuando procede Orto-K",
      "Estudio personalizado del perfil de progresión",
      "Revisiones cada 6 meses con controles exhaustivos",
    ],
    for: "Niños y adolescentes con miopía en progresión (o antecedentes familiares). Cuanto antes se empieza, mejores resultados.",
    duration: "Tratamiento continuado durante la etapa de crecimiento",
  },
  {
    slug: "sdp",
    icon: Activity,
    title: "SDP · Síndrome de Deficiencia Postural",
    subtitle: "Cuando dolor, mareos o vértigo tienen un origen visual",
    intro: "La visión dirige todos los movimientos inteligentes del cuerpo. Cuando la conexión entre el sistema visual y el propioceptivo falla, el cuerpo lo compensa con tensión muscular, dolores de cabeza, mareos, alteraciones posturales o sensación de inestabilidad. En el Síndrome de Deficiencia Postural (SDP), el origen o una parte importante del problema está en el sistema visual. En nuestro centro evaluamos este síndrome y podemos tratarlo con prismas posturales: lentes prescritas en gafa que actúan a nivel neurológico modificando las referencias espaciales del paciente, mejorando la relación entre visión, postura y conciencia corporal de forma pasiva.",
    benefits: [
      "Aliviar dolores crónicos de cabeza, cuello, hombros o espalda cuando el origen es visual",
      "Reducir mareos, vértigo o sensación de inestabilidad de causa funcional",
      "Un tratamiento pasivo: solo llevar las gafas con prismas posturales",
      "Sin medicación ni cirugía: actuación neurológica progresiva",
      "Coordinación con tu fisioterapeuta u osteópata para un abordaje completo",
    ],
    adaptationsTitle: "Cómo lo tratamos",
    adaptations: [
      "Prescripción de prismas posturales personalizada",
      "Coordinación con tu fisioterapeuta y/o osteópata",
      "Informe detallado para el equipo multidisciplinar",
      "Revisiones periódicas para ajustar la prescripción",
    ],
    includes: [
      "Cuestionario de cribado SDP organizado por captores (ojos, boca y pies)",
      "Evaluación optométrica especializada con foco en propiocepción visual",
      "Pruebas de equilibrio y referencias espaciales",
    ],
    for: "Personas con dolor crónico de cabeza, cuello, hombros o espalda sin causa estructural clara · Mareos, vértigo o sensación de inestabilidad · Mala postura al trabajar en cerca · Movimientos de cabeza al leer · Pacientes derivados por fisioterapeutas u osteópatas que sospechan un componente visual.",
    duration: "Evaluación: 60-90 min · Prescripción y seguimiento: según protocolo",
  },
  {
    slug: "wivi",
    icon: Sparkles,
    title: "WIVI · Entrenamiento visual con IA",
    subtitle: "La evolución del rendimiento visual",
    intro: "WIVI es un sistema clínico avanzado que evalúa y entrena más de 100 parámetros de la función visual combinando inteligencia artificial con tecnologías interactivas en 3D. Un método pionero, no invasivo, respaldado por autoridades sanitarias y con resultados garantizados.",
    benefits: [
      "Entrenar la visión jugando: los niños lo viven como un juego, no como una terapia",
      "Mejor rendimiento en lectura, estudio, trabajo, conducción o deporte",
      "Programa personalizado guiado por IA — se adapta a tu evolución sesión a sesión",
      "Resultados medibles: informes comparativos antes/después de las 26 sesiones",
      "Compatible con terapia visual presencial o como complemento autónomo",
    ],
    adaptationsTitle: "Cómo funciona",
    adaptations: [
      "Entrenamiento personalizado de 26 sesiones",
      "Sesiones de ~20 minutos, 2-3 veces por semana",
      "Ejercicios a través de juegos interactivos en 3D",
      "Informes de evolución comparativa",
      "Sin molestias, sin medicación, divertido y motivador",
    ],
    includes: [
      "Evaluación inicial del rendimiento visual en 12-15 minutos",
      "Análisis de más de 100 parámetros visuales con IA",
    ],
    for: "Niños y adolescentes que necesitan mejorar su rendimiento escolar o corregir dificultades de lectura y coordinación ocular · Adultos con Síndrome Visual Informático (SVI) por uso prolongado de pantallas · Cualquier persona que quiera mejorar su rendimiento visual en lectura, trabajo, conducción o deporte.",
    duration: "26 sesiones · 1 a 3 meses",
  },
  {
    slug: "audiologia-audifonos",
    icon: Ear,
    title: "Audiología y audífonos",
    subtitle: "Medir bien para oír mejor",
    intro: "La parte clínica de la audición: evaluamos con precisión tu capacidad auditiva y, cuando es necesario, adaptamos audífonos personalizados. Trabajamos con las marcas más avanzadas del mercado para ofrecerte una solución cómoda, discreta y efectiva.",
    benefits: [
      "Recuperar la conversación con tu familia sin pedir que repitan",
      "Diagnóstico claro de tu pérdida auditiva, frecuencia a frecuencia",
      "Audífonos cómodos, discretos y adaptados a tu día a día (no a una media estadística)",
      "Acompañamiento real: programación, ajustes y revisiones incluidos",
      "Mantenimiento y consumibles asegurados durante toda la vida del aparato",
    ],
    adaptationsTitle: "Adaptación y seguimiento de audífonos",
    adaptations: [
      "Adaptación de audífonos de última generación (intracanal, retroauriculares, RIC…)",
      "Programación y ajuste personalizado según tu entorno",
      "Revisiones periódicas y seguimiento continuo",
      "Limpieza, mantenimiento y reposición de consumibles",
    ],
    includes: [
      "Audiometría tonal y verbal en distintas frecuencias e intensidades",
      "Timpanometría y valoración de la vía auditiva",
      "Evaluación del procesamiento auditivo central",
      "Estudio personalizado previo a la adaptación del audífono",
    ],
    for: "Personas con pérdida auditiva de cualquier grado · Familias que notan que un ser querido sube el volumen o no escucha bien · Niños y adultos que necesitan una evaluación completa de su audición · Quienes ya llevan audífonos y buscan seguimiento profesional.",
    duration: "Audiometría: 30-45 min · Adaptación de audífono: proceso con seguimiento",
  },
  {
    slug: "terapia-auditiva",
    icon: Ear,
    title: "Terapia auditiva",
    subtitle: "Oír es recibir, escuchar es procesar",
    intro: "La audición no termina en el oído: es el cerebro quien interpreta y organiza lo que oímos. Un buen procesamiento auditivo es clave para el aprendizaje, el lenguaje, la lectura, la atención y la regulación emocional. Trabajamos con los tres métodos de reeducación auditiva con mayor aval científico.",
    benefits: [
      "Mejorar la comprensión, la atención y la memoria auditiva",
      "Acompañar dificultades de lectura, dislexia, dislalia o retraso del lenguaje desde su raíz neurológica",
      "Reducir la sensibilidad excesiva a determinados ruidos",
      "Programa elegido entre Berard, Tomatis o SENA según el perfil del paciente",
      "Coordinación con logopedas, psicólogos y equipo educativo",
    ],
    adaptationsTitle: "Métodos que utilizamos",
    adaptations: [
      "Método Berard — 20 sesiones que estimulan todas las frecuencias para madurar la ruta auditiva del cerebro",
      "Método Tomatis — estimulación neurosensorial con filtrado electrónico y conducción ósea, desarrollado por el Dr. Alfred Tomatis",
      "Método SENA — reeducación personalizada que combina estimulación auditiva con trabajo de habilidades cognitivas y lingüísticas",
      "Programa individual según el perfil del paciente, con informes de evolución y coordinación con otros profesionales",
    ],
    includes: [
      "Evaluación inicial del procesamiento auditivo: discriminación, memoria, lateralidad y atención auditiva",
    ],
    for: "Niños y adultos con dificultades de aprendizaje, lectura o escritura · Problemas de atención, comprensión o memoria auditiva · Sensibilidad excesiva a ciertos ruidos · Dislexia, dislalia, tartamudez, retraso del lenguaje · TEA y trastornos del neurodesarrollo · Personas con ansiedad o altibajos emocionales · Cantantes, estudiantes · Aprendizaje de idiomas.",
    duration: "Berard: 20 sesiones · Tomatis y SENA: según protocolo",
  },
  {
    slug: "opticas",
    icon: Glasses,
    title: "Gafas y monturas",
    subtitle: "Ver bien también es verse bien",
    intro: "Lentes oftálmicas de precisión y una selección cuidada de monturas de firmas de calidad y diseño. Asesoramiento personalizado para encontrar la gafa perfecta para ti.",
    benefits: [
      "Una gafa que te queda bien y que se ajusta a tu estilo de vida",
      "Lentes de precisión: monofocales, progresivas u ocupacionales según tu día a día",
      "Filtros y tratamientos adecuados (pantallas, sol, fotocromáticos…)",
      "Atención cercana al elegir y al ajustar — no salen contigo de la tienda hasta que te quedan perfectas",
      "Servicio post-venta: reparaciones, ajustes y revisiones",
    ],
    adaptationsTitle: "Qué encontrarás",
    adaptations: [
      "Lentes monofocales, progresivas y ocupacionales",
      "Tratamientos antirreflejantes y fotocromáticos",
      "Filtros específicos para pantallas y luz azul",
      "Gafas de sol graduadas de alta calidad",
      "Gafas infantiles resistentes y divertidas",
      "Reparaciones y ajustes",
    ],
    includes: [],
    for: "Toda la familia. Desde la primera gafa de tu hijo hasta las progresivas premium.",
    duration: "Entrega en 5-10 días laborables",
  },
];

const FAQS = [
  {
    q: "¿Esto es solo para niños con problemas de visión?",
    a: "No. Está indicado para niños, adolescentes y adultos que, aunque ven nítido, tienen dificultades para procesar lo que ven. Muchos pacientes no tienen problemas de agudeza visual, sino de coordinación ocular, enfoque, atención visual, lectura, escritura o comprensión. También es muy eficaz en casos de ojo vago, estrabismo o dificultades de aprendizaje no explicadas por otras causas.",
  },
  {
    q: "¿En qué se diferencia de usar gafas compensatorias?",
    a: "Las gafas ayudan a ver nítido, pero no enseñan al cerebro a interpretar correctamente la información visual. La terapia visual trabaja las habilidades visuales funcionales: coordinación de ambos ojos, movilidad ocular, atención visual y percepción. Es un entrenamiento para que el sistema visual funcione de forma más eficaz y eficiente, especialmente al leer, escribir o realizar actividades cotidianas.",
  },
  {
    q: "¿Cuándo se ven resultados?",
    a: "En muchos casos, las familias notan cambios en pocas semanas, especialmente en la atención, la disposición al estudio y el cansancio visual. Otros avances, como la mejora de la lectura o el comportamiento, se observan progresivamente durante el proceso. El ritmo depende de cada caso, pero siempre hacemos un seguimiento individualizado.",
  },
  {
    q: "¿Se cura el problema?",
    a: "La terapia visual no 'cura' en el sentido médico, pero sí permite reeducar y optimizar la manera en que el cerebro utiliza la visión. Nuestro objetivo es que el paciente adquiera nuevas habilidades y automatismos visuales que le permitan ver, entender y reaccionar mejor a su entorno. Es un trabajo de neuroplasticidad visual y funcional.",
  },
  {
    q: "¿Funciona en adolescentes y adultos?",
    a: "Sí. Aunque el sistema nervioso es más plástico en la infancia, en la adolescencia y adultez también se pueden conseguir grandes cambios. El tratamiento se adapta al nivel madurativo, a los objetivos personales y a la disponibilidad del paciente. Trabajamos tanto el rendimiento académico como la visión funcional y la calidad de vida.",
  },
  {
    q: "¿Es invasivo?",
    a: "No. Es un tratamiento totalmente natural, respetuoso y no invasivo. No implica medicamentos ni procedimientos dolorosos. Trabajamos con luz, estímulos visuales y ejercicios lúdicos adaptados a cada edad. Es seguro y bien tolerado incluso por niños pequeños o personas sensibles.",
  },
  {
    q: "¿Es compatible con otras terapias?",
    a: "Sí, totalmente. De hecho, muchos pacientes están también en logopedia, psicología o neuropsicología. La terapia visual potencia la eficacia de otras intervenciones al mejorar la base sensorial, la atención, la coordinación y la lectura. Siempre estamos dispuestos a trabajar en equipo con otros profesionales.",
  },
  {
    q: "¿Cómo sé que lo necesita?",
    a: "Realizamos una evaluación optométrica comportamental muy completa. No solo miramos la agudeza visual, sino también cómo enfoca, cómo mueve los ojos, cómo coordina ambos ojos, cómo interpreta lo que ve y cómo eso afecta a su aprendizaje y comportamiento. De ahí sale un informe detallado con conclusiones y propuesta de tratamiento.",
  },
  {
    q: "¿Por qué es largo el tratamiento?",
    a: "Porque buscamos una mejora real y duradera. Reeducar el sistema visual implica crear nuevas conexiones neuronales y reforzarlas con práctica. Igual que un atleta necesita meses para entrenar su cuerpo, nuestros pacientes necesitan tiempo para integrar habilidades visuales. La duración también depende del grado de dificultad y del compromiso con los ejercicios en casa.",
  },
  {
    q: "¿Qué pasa si no se hacen los ejercicios en casa?",
    a: "Los ejercicios en casa son clave para consolidar lo trabajado en consulta. Sin esa práctica, los avances pueden ser más lentos o no llegar a consolidarse. Igual que en el colegio no basta con ir a clase sin estudiar en casa, en la terapia visual el refuerzo domiciliario permite que el cerebro integre y automatice las nuevas habilidades. Te damos pautas claras, adaptadas a tu tiempo y a la edad del niño, para que sea fácil incorporarlas al día a día.",
  },
  {
    q: "¿Y si mi hijo/a no quiere hacer los ejercicios en casa?",
    a: "Es normal que a veces se muestren reacios, igual que con los deberes. Por eso adaptamos los ejercicios al estilo de cada niño y te damos estrategias para motivarlos. Recomendamos establecer los ejercicios como parte de la rutina diaria, igual que lavarse los dientes. Si aún así cuesta, lo hablamos en sesión.",
  },
  {
    q: "¿Y si falta a una sesión?",
    a: "El tratamiento es flexible. Si por alguna razón no puedes asistir, simplemente reprogramamos la sesión. No hay penalización. Lo importante es mantener la constancia dentro de un ritmo razonable para que la terapia tenga un buen efecto acumulativo.",
  },
  {
    q: "¿Puedo ver lo que hace en las sesiones?",
    a: "Durante las sesiones, por respeto a la privacidad del resto de pacientes en sala, los padres no están presentes. Sin embargo, grabamos parte de los ejercicios para que puedan practicarse en casa y damos feedback individual después de cada sesión o revisión. El contacto con la familia es directo y constante.",
  },
  {
    q: "¿El número de sesiones que nos dais al inicio es fijo?",
    a: "No necesariamente. El número recomendado al inicio se basa en la evaluación inicial y en nuestra experiencia con casos similares. Sin embargo, cada persona es única y su evolución también. A veces se necesita más tiempo para consolidar cambios, especialmente si hay múltiples áreas afectadas o si el sistema nervioso necesita más repetición para integrar los aprendizajes.",
  },
  {
    q: "¿Y si no mejora?",
    a: "Desde el inicio planteamos objetivos medibles y hacemos seguimiento continuo. Si detectamos que el progreso no es el esperado, revisamos el plan, lo ajustamos o lo complementamos con otras herramientas (Syntonic, Tomatis, WIVI…). Siempre buscamos la mayor efectividad adaptada a la evolución real del paciente.",
  },
  {
    q: "¿Cuál es la diferencia entre oftalmólogo, óptico y optometrista?",
    a: "El oftalmólogo es un médico especializado en enfermedades oculares (puede operar y recetar fármacos). El optometrista es un sanitario universitario especializado en evaluar la función visual, detectar problemas y diseñar soluciones ópticas y de terapia visual. El óptico es quien monta y adapta las gafas. En Salud y Visión trabajamos en coordinación con oftalmólogos cuando el caso lo requiere.",
  },
];

const ICONS_MAP = { Eye, Brain, Baby, Glasses, HeartPulse, Sparkles };

const ServicesPage = () => {
  useEffect(() => {
    const hash = window.location.hash.replace("#", "");
    if (hash) {
      setTimeout(() => document.getElementById(hash)?.scrollIntoView({ behavior: "smooth" }), 200);
    } else {
      window.scrollTo({ top: 0 });
    }
  }, []);

  return (
    <main data-testid="services-page">
      {/* HERO */}
      <section className="py-20 px-6" style={{ background: "var(--bg-soft)" }}>
        <div className="max-w-5xl mx-auto fade-up">
          <span className="section-eyebrow">Nuestros servicios</span>
          <h1 className="section-title mt-4">
            Un abordaje <span className="handwritten">integral</span>
            <br />de la visión
          </h1>
          <p className="font-sans-body text-[color:var(--ink-soft)] mt-6 max-w-2xl text-lg">
            Desde el examen optométrico más completo hasta la rehabilitación tras una lesión cerebral. Conoce todo lo que podemos hacer por ti.
          </p>
          <div className="mt-8 flex flex-wrap gap-2">
            {SERVICES_DETAIL.map((s) => (
              <a key={s.slug} href={`#${s.slug}`} className="px-4 py-2 rounded-full font-sans-body text-sm transition-colors" style={{ background: "var(--cream)", color: "var(--sage-600)", border: "1px solid var(--sage-100)" }} data-testid={`quick-link-${s.slug}`}>
                {s.title}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICE SECTIONS */}
      <section className="py-20 px-6">
        <div className="max-w-5xl mx-auto space-y-24">
          {SERVICES_DETAIL.map((s, idx) => (
            <article key={s.slug} id={s.slug} className="scroll-mt-24" data-testid={`service-detail-${s.slug}`}>
              <div className="grid md:grid-cols-12 gap-10">
                <div className="md:col-span-4">
                  <div className="sticky top-28">
                    <div className="h-16 w-16 rounded-2xl flex items-center justify-center mb-5" style={{ background: "var(--sage-500)" }}>
                      <s.icon size={28} style={{ color: "var(--cream)" }} />
                    </div>
                    <div className="section-eyebrow">0{idx + 1}</div>
                    <h2 className="text-3xl font-medium mt-2" style={{ color: "var(--ink)" }}>{s.title}</h2>
                    <p className="font-display text-2xl mt-3" style={{ color: "var(--sage-500)" }}>{s.subtitle}</p>
                    {s.duration && (
                      <div className="mt-6 flex items-center gap-2 font-sans-body text-sm" style={{ color: "var(--ink-soft)" }}>
                        <Clock size={16} style={{ color: "var(--sage-500)" }} /> {s.duration}
                      </div>
                    )}
                  </div>
                </div>

                <div className="md:col-span-8">
                  <p className="font-sans-body text-[color:var(--ink-soft)] leading-relaxed text-lg">{s.intro}</p>

                  {s.benefits ? (
                    <>
                      <div className="mt-8">
                        <h3 className="section-eyebrow flex items-center gap-2"><Zap size={14} /> Qué consigues</h3>
                        <ul className="mt-4 space-y-3 font-sans-body">
                          {s.benefits.map((item, i) => (
                            <li key={i} className="flex gap-3 items-start">
                              <CheckCircle2 size={18} className="mt-0.5 shrink-0" style={{ color: "var(--sage-500)" }} />
                              <span className="text-[color:var(--ink)]">{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {s.adaptations && (
                        <div className="mt-8">
                          <h3 className="section-eyebrow flex items-center gap-2"><Glasses size={14} /> {s.adaptationsTitle || "Tipos de adaptaciones que realizamos"}</h3>
                          <ul className="mt-4 space-y-3 font-sans-body">
                            {s.adaptations.map((item, i) => (
                              <li key={i} className="flex gap-3 items-start">
                                <CheckCircle2 size={18} className="mt-0.5 shrink-0" style={{ color: "var(--sage-500)" }} />
                                <span className="text-[color:var(--ink)]">{item}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {s.includes && s.includes.length > 0 && (
                        <details className="mt-6 group rounded-2xl" style={{ border: "1px solid var(--sage-100)", background: "var(--bg-soft)" }} data-testid={`includes-toggle-${s.slug}`}>
                          <summary className="cursor-pointer list-none flex items-center justify-between p-5 font-sans-body text-sm font-semibold select-none" style={{ color: "var(--sage-700)" }}>
                            <span className="flex items-center gap-2">
                              <Activity size={16} style={{ color: "var(--sage-500)" }} />
                              {s.includesTitle || "Ver pruebas clínicas que realizamos"} ({s.includes.length})
                            </span>
                            <span className="transition-transform group-open:rotate-45 text-xl leading-none" style={{ color: "var(--sage-500)" }}>+</span>
                          </summary>
                          <ul className="px-5 pb-5 space-y-3 font-sans-body">
                            {s.includes.map((item, i) => (
                              <li key={i} className="flex gap-3 items-start">
                                <CheckCircle2 size={16} className="mt-0.5 shrink-0" style={{ color: "var(--sage-500)" }} />
                                <span className="text-sm text-[color:var(--ink-soft)]">{item}</span>
                              </li>
                            ))}
                          </ul>
                        </details>
                      )}
                    </>
                  ) : (
                    <div className="mt-8">
                      <h3 className="section-eyebrow flex items-center gap-2"><Zap size={14} /> Qué incluye</h3>
                      <ul className="mt-4 space-y-3 font-sans-body">
                        {s.includes.map((item, i) => (
                          <li key={i} className="flex gap-3 items-start">
                            <CheckCircle2 size={18} className="mt-0.5 shrink-0" style={{ color: "var(--sage-500)" }} />
                            <span className="text-[color:var(--ink)]">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="mt-8 p-5 rounded-2xl" style={{ background: "var(--sage-50)", border: "1px solid var(--sage-100)" }}>
                    <h3 className="section-eyebrow flex items-center gap-2"><Users size={14} /> Indicado para</h3>
                    <p className="mt-2 font-sans-body text-[color:var(--ink-soft)]">{s.for}</p>
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* VISIÓN TOTAL — Programa integral */}
      <section className="py-24 px-6 relative overflow-hidden" id="vision-total" data-testid="vision-total">
        <div className="absolute top-1/4 right-10 font-display text-9xl opacity-10" style={{ color: "var(--sage-500)" }}>✦</div>
        <div className="max-w-6xl mx-auto relative">
          <div className="grid md:grid-cols-12 gap-10 items-start">
            <div className="md:col-span-5">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full font-sans-body text-xs font-semibold" style={{ background: "var(--sage-500)", color: "var(--cream)" }}>
                ✦ Programa estrella
              </div>
              <h2 className="section-title mt-5">
                <span className="handwritten">Visión Total</span>
                <br />el programa integral
              </h2>
              <p className="font-sans-body text-[color:var(--ink-soft)] mt-5 leading-relaxed">
                Muchos niños y adultos tienen dificultades para aprender, trabajar, concentrarse o disfrutar de su día a día — y muchas veces la causa está en cómo ven y procesan la información.
              </p>
              <p className="font-sans-body text-[color:var(--ink-soft)] mt-4 leading-relaxed">
                Por eso diseñamos un programa que combina lo mejor de la optometría comportamental, la terapia visual y auditiva, y un acompañamiento cercano a las familias. Un cambio de vida real y duradero.
              </p>
              <div className="mt-8 space-y-2 font-sans-body text-sm">
                {[
                  "Menos frustración y más facilidad para aprender",
                  "Más confianza en uno mismo",
                  "Menos tensión en casa",
                  "Una nueva forma de mirar el mundo y entenderlo",
                ].map((x, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <span className="font-display text-lg" style={{ color: "var(--sage-500)" }}>✦</span>
                    <span className="text-[color:var(--ink)]">{x}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="md:col-span-7">
              <div className="rounded-3xl p-8 md:p-10" style={{ background: "var(--sage-500)", color: "var(--cream)" }}>
                <div className="section-eyebrow" style={{ color: "var(--sage-200)" }}>Qué incluye el programa</div>
                <div className="mt-6 space-y-5">
                  {[
                    { n: "01", t: "Fototerapia Syntonic", d: "Terapia con luz que equilibra el sistema nervioso mediante frecuencias específicas del espectro. Mejora atención, visión periférica, sueño y concentración." },
                    { n: "02", t: "WIVI · 26 sesiones", d: "Entrenamiento de la coordinación binocular con inteligencia artificial. Mejora lectura, confort visual y precisión deportiva." },
                    { n: "03", t: "Terapia visual activa", d: "Sesiones individuales de 45 minutos con optometrista especializado. Ejercicios personalizados, grabados para practicar en casa." },
                    { n: "04", t: "Kit de material + acompañamiento", d: "Material para ejercicios en casa, análisis de videos de devolución, informes detallados, reuniones con el cole y sesiones de seguimiento." },
                  ].map((x) => (
                    <div key={x.n} className="flex gap-4">
                      <div className="font-display text-3xl shrink-0 w-12" style={{ color: "var(--sage-200)" }}>{x.n}</div>
                      <div>
                        <div className="text-lg font-medium">{x.t}</div>
                        <p className="font-sans-body text-sm mt-1 opacity-85">{x.d}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-8 pt-6 border-t border-white/20">
                  <a href="/#contacto" className="inline-flex items-center gap-2 rounded-full px-6 py-3 font-medium text-sm" style={{ background: "var(--cream)", color: "var(--sage-600)" }} data-testid="vision-total-cta">
                    Consultar Visión Total <ArrowRight size={16} />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 px-6" style={{ background: "var(--sage-50)" }}>
        <div className="max-w-4xl mx-auto">
          <div className="text-center">
            <span className="section-eyebrow">Preguntas frecuentes</span>
            <h2 className="section-title mt-4">Todo lo que te <span className="handwritten">preguntas</span></h2>
          </div>
          <div className="mt-12 space-y-4">
            {FAQS.map((f, i) => (
              <details key={i} className="soft-card group" data-testid={`faq-${i}`}>
                <summary className="cursor-pointer flex justify-between items-start gap-4 list-none">
                  <span className="text-lg font-medium" style={{ color: "var(--ink)" }}>{f.q}</span>
                  <span className="font-display text-2xl group-open:rotate-45 transition-transform" style={{ color: "var(--sage-500)" }}>✦</span>
                </summary>
                <p className="mt-4 font-sans-body text-[color:var(--ink-soft)] leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA — Autoevaluación */}
      <VisualCheckup />
    </main>
  );
};

export default ServicesPage;
